import type {
  PlatformInvitationV1,
  PlatformOrganizationAccessV1,
  PlatformOrganizationV1,
} from "@strust/contracts";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useApiClient } from "../../../shared/api/api-provider.tsx";
import {
  createOperatorInOrganizationEndpoint,
  createOrganizationEndpoint,
  getOperatorOrganizationEndpoint,
  getOrganizationAccessEndpoint,
  inviteFirstAdministratorEndpoint,
  invitePlatformAdministratorEndpoint,
  listOrganizationsEndpoint,
  listPendingInvitationsEndpoint,
  revokePlatformInvitationEndpoint,
} from "./endpoints.ts";
import { operatorsKey } from "./operators.ts";

export const organizationsKey = ["platform", "organizations"] as const;
/** Every organization of the installation, every page read (design D2). */
export const allOrganizationsKey = [...organizationsKey, "all"] as const;
export const invitationsKey = ["platform", "invitations"] as const;
/** Every pending platform invitation, of every kind (design D2). */
export const pendingInvitationsKey = [...invitationsKey, "pending"] as const;
export const operatorOrganizationKey = (operatorId: string) =>
  ["platform", "operator-organization", operatorId] as const;
const OPERATOR_ORGANIZATIONS_KEY = ["platform", "operator-organization"] as const;
const ORGANIZATION_ACCESS_KEY = ["platform", "organization-access"] as const;
/** The access overview of one organization (design D2, D16). */
export const organizationAccessKey = (organizationId: string) =>
  [...ORGANIZATION_ACCESS_KEY, organizationId] as const;

/** The largest page the list routes serve. */
export const ORGANIZATIONS_PAGE_SIZE = 100;
export const INVITATIONS_PAGE_SIZE = 20;
const ALL_INVITATIONS_PAGE_SIZE = 100;

/**
 * Reads every page of a paged list route, stopping once `page * size` reaches the total (or a page
 * comes back empty).
 */
async function readAllPages<T>(
  read: (page: number) => Promise<{ readonly items: readonly T[]; readonly total: number }>,
  size: number,
): Promise<T[]> {
  const items: T[] = [];
  for (let page = 1; ; page += 1) {
    const found = await read(page);
    items.push(...found.items);
    if (found.items.length === 0 || page * size >= found.total) return items;
  }
}

/**
 * Every organization with its linked operators, ordered by name (design D2): the list is small (one
 * row per customer group) and the route has no search, so search, selection and counts are computed
 * on the client.
 */
export function useAllOrganizations(enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: allOrganizationsKey,
    enabled,
    queryFn: ({ signal }) =>
      readAllPages<PlatformOrganizationV1>(
        (page) =>
          client.call(listOrganizationsEndpoint, {
            query: { page, size: ORGANIZATIONS_PAGE_SIZE },
            signal,
          }),
        ORGANIZATIONS_PAGE_SIZE,
      ),
  });
}

/**
 * The organization an operator belongs to, also after it is deactivated (`organization: null` only
 * for an operator created before every operator had one and not adopted yet).
 */
export function useOperatorOrganization(operatorId: string, enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: operatorOrganizationKey(operatorId),
    enabled,
    queryFn: ({ signal }) =>
      client.call(getOperatorOrganizationEndpoint, { params: { id: operatorId }, signal }),
  });
}

/**
 * Every pending platform invitation of every kind, newest first (design D2, D8): one query shared by
 * the Access tab of an organization (its first-Administrator invitations) and the Platform
 * Administrators screen (all of them), so a revocation in one place removes the row in both.
 * Pending invitations live 72 h, so the list stays short.
 */
export function usePendingInvitations(enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: pendingInvitationsKey,
    enabled,
    queryFn: ({ signal }) =>
      readAllPages<PlatformInvitationV1>(
        (page) =>
          client.call(listPendingInvitationsEndpoint, {
            query: { status: "pending", page, size: ALL_INVITATIONS_PAGE_SIZE },
            signal,
          }),
        ALL_INVITATIONS_PAGE_SIZE,
      ),
  });
}

/** The pending first-Administrator invitations of one organization (its Access tab). */
export function firstAdministratorInvitationsOf(
  invitations: readonly PlatformInvitationV1[],
  organizationId: string,
): readonly PlatformInvitationV1[] {
  return invitations.filter(
    (i) => i.kind === "first_administrator" && i.organization_id === organizationId,
  );
}

/** The access overview of one organization (design D16); `enabled` is false without the permission. */
export function useOrganizationAccess(organizationId: string | undefined, enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: organizationAccessKey(organizationId ?? ""),
    enabled: enabled && organizationId !== undefined,
    queryFn: ({ signal }) =>
      client.call(getOrganizationAccessEndpoint, {
        params: { id: organizationId ?? "" },
        signal,
      }),
  });
}

/**
 * The access overviews of the listed organizations, for their status badges (design D2, D3): one
 * cached request per organization, the same cache as {@link useOrganizationAccess}. Answers a map
 * from organization id to its overview; an organization still loading or failed is absent (no
 * status is guessed).
 */
export function useOrganizationsAccess(
  organizationIds: readonly string[],
  enabled: boolean,
): ReadonlyMap<string, PlatformOrganizationAccessV1> {
  const client = useApiClient();
  return useQueries({
    queries: organizationIds.map((id) => ({
      queryKey: organizationAccessKey(id),
      enabled,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        client.call(getOrganizationAccessEndpoint, { params: { id }, signal }),
    })),
    combine: (results) =>
      new Map(
        results.flatMap((result, index) => {
          const id = organizationIds[index];
          return result.data && id !== undefined ? [[id, result.data] as const] : [];
        }),
      ),
  });
}

/** Refreshes what an invitation changes: both invitation views and the organizations' access. */
function invalidateInvitations(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: invitationsKey }),
    queryClient.invalidateQueries({ queryKey: ORGANIZATION_ACCESS_KEY }),
  ]);
}

export function useCreateOrganization() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => client.call(createOrganizationEndpoint, { body: { name } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationsKey }),
  });
}

/**
 * Creates an operator inside an organization (design D1/D3): the answer has only the ids, so the
 * operators and organizations are refetched (awaited, so the new operator is listed when the
 * caller's `onSuccess` runs and offers its first-Administrator invitation).
 */
export function useCreateOperatorInOrganization() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { organizationId: string; slug: string; name: string }) =>
      client.call(createOperatorInOrganizationEndpoint, {
        params: { id: input.organizationId },
        body: { slug: input.slug, name: input.name },
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationsKey }),
        queryClient.invalidateQueries({ queryKey: operatorsKey }),
        queryClient.invalidateQueries({ queryKey: OPERATOR_ORGANIZATIONS_KEY }),
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_ACCESS_KEY }),
      ]),
  });
}

export function useRevokePlatformInvitation() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      client.call(revokePlatformInvitationEndpoint, { params: { id: invitationId }, body: {} }),
    // A `410` means it was already revoked: the lists are stale either way.
    onSettled: () => invalidateInvitations(queryClient),
  });
}

export function useInviteFirstAdministrator() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { organizationId: string; operatorId: string; email: string }) =>
      client.call(inviteFirstAdministratorEndpoint, {
        params: { id: input.organizationId },
        body: { operator_id: input.operatorId, email: input.email },
      }),
    onSuccess: () => invalidateInvitations(queryClient),
    // The response carries the single-use token: it is not kept in the mutation cache.
    gcTime: 0,
  });
}

export function useInvitePlatformAdministrator() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      client.call(invitePlatformAdministratorEndpoint, { body: { email } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invitationsKey }),
    gcTime: 0,
  });
}
